import { useState } from 'react';
import {
  accountCredentialsSchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { ApiError, request, savedSchema, idSchema, type Session } from './api';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import { useRemote } from './hooks';
import { useDraftNavigation } from './draft-navigation';
import type { Copy } from './copy';

export function AccountCredentials({
  scope,
  session,
  copy,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
}) {
  const path = `/v1/accounts/${scope.companyId}/credentials`;
  const remote = useRemote(path, accountCredentialsSchema);
  const mutation = useMutation();
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [jevKey, setJevKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [visible, setVisible] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [leaveError, setLeaveError] = useState<Error | null>(null);
  useDraftNavigation(dirty, async () => {
    const error = new ApiError('CREDENTIAL_FORM_UNSAVED', 409);
    setLeaveError(error);
    throw error;
  });
  function close() {
    setDirty(false);
    setLeaveError(null);
    setVisible(false);
    setEditing(null);
    setName('');
    setOpenaiKey('');
    setJevKey('');
    setAnthropicKey('');
  }
  return (
    <section className="settings-content">
      <div className="section-heading">
        <h1>{copy.accountAiProviders}</h1>
        <p>{copy.accountKeysHelp}</p>
      </div>
      <ErrorNotice
        error={remote.error ?? mutation.error ?? leaveError}
        copy={copy}
        onRetry={remote.reload}
      />
      {remote.loading && !remote.data && <p>{copy.loading}</p>}
      {saved && (
        <p role="status" className="notice success">
          {copy.modelCredentialsSaved}
        </p>
      )}
      {remote.data && (
        <>
          <div className="section-toolbar">
            <h2>{copy.credentialProfiles}</h2>
            <button
              disabled={dirty}
              onClick={() => {
                close();
                setVisible(true);
                setSaved(false);
              }}
            >
              {copy.addCredentialProfile}
            </button>
          </div>
          {remote.data.profiles.length === 0 && (
            <p className="empty-description">{copy.noCredentialProfiles}</p>
          )}
          {remote.data.profiles.map((profile) => (
            <div className="connection-row" key={profile.id}>
              <div>
                <strong>{profile.name}</strong>
                <p className="muted small">
                  {profile.anthropicConfigured
                    ? copy.credentialsEncryptedWithAnthropic
                    : copy.credentialsEncrypted}
                </p>
              </div>
              <div className="row-actions">
                <button
                  disabled={dirty}
                  onClick={() => {
                    close();
                    setEditing(profile.id);
                    setName(profile.name);
                    setVisible(true);
                    setSaved(false);
                  }}
                >
                  {copy.edit}
                </button>
                <button
                  disabled={
                    mutation.busy ||
                    remote.data!.assignments.some(
                      (item) => item.profileId === profile.id
                    )
                  }
                  onClick={() =>
                    void mutation.run(async () => {
                      await request(`${path}/${profile.id}`, savedSchema, {
                        method: 'DELETE',
                        csrf: session.csrf,
                        body: {},
                      });
                      remote.reload();
                    })
                  }
                >
                  {copy.remove}
                </button>
              </div>
            </div>
          ))}
          {visible && (
            <form
              className="edit-panel"
              onChange={() => setDirty(true)}
              onSubmit={(event) =>
                onSubmit(event, () =>
                  mutation.run(async () => {
                    await request(path, idSchema, {
                      csrf: session.csrf,
                      body: {
                        ...(editing
                          ? { id: editing }
                          : { encryptionBotId: scope.botId }),
                        name,
                        ...(openaiKey.trim()
                          ? { openaiKey: openaiKey.trim() }
                          : {}),
                        ...(jevKey.trim() ? { jevKey: jevKey.trim() } : {}),
                        ...(anthropicKey.trim()
                          ? { anthropicKey: anthropicKey.trim() }
                          : {}),
                      },
                    });
                    close();
                    setSaved(true);
                    remote.reload();
                  })
                )
              }
            >
              <fieldset disabled={mutation.busy}>
                <Field label={copy.credentialProfileName}>
                  <input
                    autoFocus
                    required
                    maxLength={100}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
                <p className="small muted">
                  {editing
                    ? copy.replaceSharedKeysHelp
                    : copy.newSharedKeysHelp}
                </p>
                <div className="form-grid">
                  <Field label={copy.openaiApiKey}>
                    <input
                      type="password"
                      autoComplete="new-password"
                      required={!editing}
                      maxLength={4096}
                      value={openaiKey}
                      onChange={(event) => setOpenaiKey(event.target.value)}
                    />
                  </Field>
                  <Field label={copy.jevApiKey}>
                    <input
                      type="password"
                      autoComplete="new-password"
                      required={!editing}
                      maxLength={4096}
                      value={jevKey}
                      onChange={(event) => setJevKey(event.target.value)}
                    />
                  </Field>
                  <div>
                    <Field label={copy.anthropicApiKey}>
                      <input
                        type="password"
                        autoComplete="new-password"
                        maxLength={4096}
                        value={anthropicKey}
                        onChange={(event) =>
                          setAnthropicKey(event.target.value)
                        }
                      />
                    </Field>
                    <p className="small muted field-hint">
                      {copy.anthropicKeyHelp}
                    </p>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="primary">{copy.save}</button>
                  <button type="button" onClick={close}>
                    {copy.cancel}
                  </button>
                </div>
              </fieldset>
            </form>
          )}
          <section className="form-section">
            <h2>{copy.credentialAssignments}</h2>
            <p>{copy.modelCredentialStrict}</p>
            {remote.data.assignments.map((assignment) => (
              <CredentialAssignment
                key={`${assignment.botId}/${assignment.profileId}/${assignment.mode}`}
                assignment={assignment}
                profiles={remote.data!.profiles}
                scope={scope}
                session={session}
                copy={copy}
                reload={remote.reload}
              />
            ))}
          </section>
        </>
      )}
    </section>
  );
}

function CredentialAssignment({
  assignment,
  profiles,
  scope,
  session,
  copy,
  reload,
}: {
  assignment: (typeof accountCredentialsSchema._output.assignments)[number];
  profiles: typeof accountCredentialsSchema._output.profiles;
  scope: BotScope;
  session: Session;
  copy: Copy;
  reload: () => void;
}) {
  const [profileId, setProfileId] = useState(
    assignment.mode === 'platform' ? '' : (assignment.profileId ?? '')
  );
  const mutation = useMutation();
  const [leaveError, setLeaveError] = useState<Error | null>(null);
  const [savedProfileId, setSavedProfileId] = useState(
    assignment.mode === 'platform' ? '' : (assignment.profileId ?? '')
  );
  const dirty = profileId !== savedProfileId;
  useDraftNavigation(dirty, async () => {
    const error = new ApiError('CREDENTIAL_FORM_UNSAVED', 409);
    setLeaveError(error);
    throw error;
  });
  return (
    <form
      className="assignment-row"
      onSubmit={(event) =>
        onSubmit(event, () =>
          mutation.run(async () => {
            await request(
              `/v1/accounts/${scope.companyId}/credential-assignments`,
              savedSchema,
              {
                csrf: session.csrf,
                body: {
                  botId: assignment.botId,
                  mode: profileId ? 'bot_required' : 'platform',
                  profileId: profileId || null,
                },
              }
            );
            setSavedProfileId(profileId);
            setLeaveError(null);
            reload();
          })
        )
      }
    >
      <Field label={assignment.name}>
        <select
          value={profileId}
          onChange={(event) => setProfileId(event.target.value)}
        >
          <option value="">{copy.modelCredentialPlatform}</option>
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name} · {copy.ownKeysRequired}
            </option>
          ))}
        </select>
      </Field>
      <button
        disabled={
          mutation.busy ||
          profileId ===
            (assignment.mode === 'platform' ? '' : assignment.profileId)
        }
      >
        {copy.save}
      </button>
      {dirty && (
        <button
          type="button"
          disabled={mutation.busy}
          onClick={() => {
            setProfileId(savedProfileId);
            setLeaveError(null);
          }}
        >
          {copy.cancel}
        </button>
      )}
      <ErrorNotice error={mutation.error ?? leaveError} copy={copy} />
    </form>
  );
}
